import _ from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { P2P_MARKETS_PER_PAGE } from '@/config/p2p'
import { useGetP2pMarketsQuery } from '@/store/api/p2pApi'
import type { P2PMarket, P2PMarketsRequest } from '@/types/p2p'

type MarketFilters = Pick<P2PMarketsRequest, 'order' | 'status' | 'search'>

interface PaginationState {
	key: string
	page: number
}

interface MarketResults {
	key: string
	markets: P2PMarket[]
	total: number | null
	lastPageCount: number
}

export function useInfiniteP2pMarkets(filters: MarketFilters) {
	const filterKey = JSON.stringify(filters)
	const [pagination, setPagination] = useState<PaginationState>({ key: filterKey, page: 1 })
	const [results, setResults] = useState<MarketResults>({ key: filterKey, markets: [], total: null, lastPageCount: 0 })
	const [expireAt] = useState(() => new Date().toISOString())
	const page = pagination.key === filterKey ? pagination.page : 1

	const { data, isLoading, isError, isFetching } = useGetP2pMarketsQuery({
		page,
		per_page: P2P_MARKETS_PER_PAGE,
		expire_at: expireAt,
		...filters,
	})

	useEffect(() => {
		if (!data) {
			return
		}

		setResults((previousResults) => {
			const total = typeof data.total === 'number' ? data.total : page === 1 ? null : previousResults.total
			if (page === 1 || previousResults.key !== filterKey) {
				return { key: filterKey, markets: data.data, total, lastPageCount: data.data.length }
			}

			const existingIds = new Set(_.map(previousResults.markets, 'id'))
			const appendedMarkets = _.filter(data.data, (market) => !existingIds.has(market.id))

			return {
				key: filterKey,
				markets: [...previousResults.markets, ...appendedMarkets],
				total,
				lastPageCount: data.data.length,
			}
		})
	}, [data, filterKey, page])

	const markets = results.key === filterKey ? results.markets : []
	const total = results.key === filterKey ? results.total : null
	const lastPageCount = results.key === filterKey ? results.lastPageCount : 0

	const hasMore = total != null ? markets.length < total : lastPageCount === P2P_MARKETS_PER_PAGE

	const loadMore = useCallback(() => {
		if (!hasMore || isFetching) {
			return
		}

		setPagination((previousPagination) => ({
			key: filterKey,
			page: previousPagination.key === filterKey ? previousPagination.page + 1 : 2,
		}))
	}, [filterKey, hasMore, isFetching])

	return {
		markets,
		isLoading: page === 1 && (isLoading || (isFetching && markets.length === 0)),
		isLoadingMore: isFetching && page > 1,
		isError,
		hasMore,
		loadMore,
	}
}
